/**
 * Monetisation intent-experiment types (MONETISATION_INTENT_EXPERIMENT.md).
 *
 * The intent gate shown on export collects three signals. These are the
 * canonical option lists so the form, tracking, and persistence all share one
 * source of truth. PII (email) is never sent to analytics — only the
 * non-identifying dimensions below flow through `trackIntentCaptured`.
 */

export type IntentSegment =
  | 'recruiter'
  | 'hr'
  | 'payroll'
  | 'accountant'
  | 'individual'
  | 'other';

export type WillingnessToPay =
  | 'very_likely'
  | 'somewhat_likely'
  | 'not_likely'
  | 'not_sure';

export interface IntentRecord {
  email: string;
  segment: IntentSegment;
  willingnessToPay: WillingnessToPay;
  capturedAt?: string;
}

/**
 * Snapshot of the calculation that triggered an export submit. Carried on the
 * monetisation lead record (AC fx-2.2#AC2) so the captured lead has the
 * context that produced it. Stored verbatim on the lead-capture payload; never
 * sent to analytics (only the non-identifying WTP dimension is).
 */
export interface CalculationContextSnapshot {
  desiredNetIncome: number;
  grossIncome: number;
  netIncome: number;
  inputFrequency: 'monthly' | 'yearly';
  taxYear?: string;
}

/**
 * Full lead record written to the lead-capture path on a valid submit.
 * Carries email, segment, WTP response, and the triggering calculation
 * context (AC fx-2.2#AC2).
 */
export interface MonetisationLeadRecord {
  email: string;
  segment: IntentSegment;
  willingnessToPay: WillingnessToPay;
  calculationContext?: CalculationContextSnapshot;
  source: 'reverseTax-b2b';
  capturedAt?: string;
}

export const INTENT_SEGMENTS: { value: IntentSegment; label: string }[] = [
  { value: 'recruiter', label: 'Recruiter' },
  { value: 'hr', label: 'HR' },
  { value: 'payroll', label: 'Payroll' },
  { value: 'accountant', label: 'Accountant' },
  { value: 'individual', label: 'Individual' },
  { value: 'other', label: 'Other' },
];

export const WTP_OPTIONS: { value: WillingnessToPay; label: string }[] = [
  { value: 'very_likely', label: 'Very likely to pay' },
  { value: 'somewhat_likely', label: 'Somewhat likely to pay' },
  { value: 'not_likely', label: 'Not likely to pay' },
  { value: 'not_sure', label: 'Not sure yet' },
];
